import { useEffect, useRef, useState } from "react";
import { GlobalWorkerOptions, getDocument, type PDFDocumentProxy } from "pdfjs-dist";

import { cn } from "../lib/utils.js";
import { logError } from "../lib/logger.js";

GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url).toString();

const NEAR_BOTTOM_PX = 24;

// Renders a policy document (always a PDF; Word uploads are converted on the
// server) page by page in a scrolling box, and reports once the reader has
// scrolled to the end. Used inline on every signing screen (PolicySignPanel).
export function PolicyDocumentViewer({ url, onReachedEnd, className }: {
  url: string;
  onReachedEnd?: () => void;
  /** Height and framing; the box scrolls inside it. */
  className?: string;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const reachedEndRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  function checkScrollPosition() {
    const el = scrollRef.current;
    if (!el || reachedEndRef.current) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - NEAR_BOTTOM_PX) {
      reachedEndRef.current = true;
      onReachedEnd?.();
    }
  }

  useEffect(() => {
    let cancelled = false;
    let doc: PDFDocumentProxy | null = null;
    reachedEndRef.current = false;

    async function render() {
      setLoading(true); setError("");
      try {
        doc = await getDocument(url).promise;
        if (cancelled) return;
        const container = scrollRef.current;
        if (!container) return;
        container.innerHTML = "";

        for (let pageNum = 1; pageNum <= doc.numPages; pageNum++) {
          if (cancelled) return;
          const page = await doc.getPage(pageNum);
          const viewport = page.getViewport({ scale: 1.4 });
          const canvas = document.createElement("canvas");
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.className = "mx-auto mb-3 block max-w-full shadow-sm";
          const ctx = canvas.getContext("2d");
          if (!ctx) continue;
          await page.render({ canvasContext: ctx, viewport }).promise;
          if (cancelled) return;
          container.appendChild(canvas);
        }

        if (!cancelled) checkScrollPosition();
      } catch (err) {
        if (!cancelled) {
          logError("PolicyDocumentViewer.render", err, { url });
          setError((err as { name?: string })?.name === "InvalidPDFException"
            ? "This document isn't a PDF, so it can't be shown. Ask HR to upload it again."
            : "Could not load the document. Please try again.");
        }
      }
      if (!cancelled) setLoading(false);
    }

    void render();
    return () => {
      cancelled = true;
      void doc?.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  if (error) {
    return <div className={cn("flex items-center justify-center p-6 text-center text-sm text-[var(--color-error-fg)]", className)}>{error}</div>;
  }

  return (
    <div className={cn("relative min-h-0", className)}>
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-surface-100 text-[13px] text-surface-500">Loading document…</div>
      )}
      <div ref={scrollRef} onScroll={checkScrollPosition} className="h-full overflow-y-auto bg-surface-100 p-4" />
    </div>
  );
}
